// ClinicOS — i18n Internationalization System
// Lightweight i18n without external dependencies
// Supports es (Spanish) and en (English)

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { es } from './es';
import { en } from './en';

export type Locale = 'es' | 'en';

const translations: Record<Locale, Record<string, string>> = { es, en };

interface I18nState {
    locale: Locale;
    t: (key: string, vars?: Record<string, string>) => string;
    setLocale: (l: Locale) => void;
}

const I18nContext = createContext<I18nState>({
    locale: 'es',
    t: (key) => key,
    setLocale: () => { },
});

export function I18nProvider({ children, defaultLocale = 'es' }: { children: ReactNode; defaultLocale?: Locale }) {
    const [locale, setLocaleState] = useState<Locale>(
        () => (localStorage.getItem('clinicos_locale') as Locale) || defaultLocale
    );

    const setLocale = useCallback((l: Locale) => {
        setLocaleState(l);
        localStorage.setItem('clinicos_locale', l);
        document.documentElement.lang = l;
    }, []);

    const t = useCallback((key: string, vars?: Record<string, string>) => {
        let text = translations[locale]?.[key] || translations['es']?.[key] || key;
        if (vars) {
            for (const [k, v] of Object.entries(vars)) {
                text = text.replace(`{${k}}`, v);
            }
        }
        return text;
    }, [locale]);

    return (
        <I18nContext.Provider value={{ locale, t, setLocale }}>
            {children}
        </I18nContext.Provider>
    );
}

export function useI18n() {
    return useContext(I18nContext);
}

export function LanguageToggle() {
    const { locale, setLocale } = useI18n();

    return (
        <button
            onClick={() => setLocale(locale === 'es' ? 'en' : 'es')}
            title={locale === 'es' ? 'Switch to English' : 'Cambiar a Español'}
            style={{
                background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
                color: '#fff', padding: '4px 10px', borderRadius: 6, fontSize: 12,
                cursor: 'pointer', fontWeight: 600, transition: '150ms',
            }}
        >
            {locale === 'es' ? '🇺🇸 EN' : '🇨🇴 ES'}
        </button>
    );
}
